import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import {
  IActivo,
  IFilter,
  IGeoJSONPoint,
  IQueryParam,
  IReporteGenerico,
} from 'modelos/src';
import { Subscription, firstValueFrom, retry, timer } from 'rxjs';
import {
  LineStringMapa,
  MarkerMapa,
} from '../../../../../auxiliares/mapa-facil/interfaces';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { MapCardConfig } from '../../../../../componentes/map-card/map-card.component';
import {
  EstadoConexion,
  duracion,
  estadoConexion,
  haceCuanto,
  minutosDesde,
} from '../../../../../auxiliares/estado-conexion.util';
import { isModoDesactivadoActivo } from '../../../../../auxiliares/modo-desactivado.util';

type Rango = 'ultimo' | '1h' | '6h' | '12h' | '24h' | '72h';

/** Lo que muestra la pestaña Info, ya armado para la plantilla. */
interface VistaActivo {
  conexion: EstadoConexion;
  hace?: string;
  ultimoReporte?: string;
  /** Solo si el equipo la reporta (los T1000-B son tags a batería). */
  bateria?: string;
  agrupacion?: string;
  fechaAlta?: string;
  estadoCuenta?: string;
  /** Hasta cuándo, si el modo desactivado está vigente. */
  desactivado?: string;
  fotos: string[];
  tracker?: {
    id?: string;
    nombre?: string;
    tipo?: string;
    uniqueId?: string;
    deveui?: string;
    frecuencia?: string;
  };
}

@Component({
  selector: 'app-detalles-activo-info',
  templateUrl: './detalles-activo-info.component.html',
  styleUrl: './detalles-activo-info.component.scss',
  standalone: false,
})
export class DetallesActivoInfoComponent implements OnChanges, OnDestroy {
  private helper = inject(HelperService);
  private lista2 = inject(Lista2Service);

  /** Lo pide la página; Info ya no lo vuelve a pedir. */
  @Input() activo?: IActivo;

  public puedeVerTracker = this.helper.tienePermiso(
    'Administración - Ver trackers',
  );

  public vista?: VistaActivo;
  /** El último reporte de cualquier tipo: conexión y "Último reporte". */
  private reporteReciente?: IReporteGenerico;
  /** El último con posición: el mapa. */
  private reportePosicion?: IReporteGenerico;
  private reportes$?: Subscription;
  private idPedido?: string;

  public mapCardConfig: MapCardConfig = {
    title: 'Última ubicación',
    icon: 'location_on',
    color: 'red',
  };
  public loadingMapa = signal(true);

  public rangos: { valor: Rango; etiqueta: string; horas: number }[] = [
    { valor: 'ultimo', etiqueta: 'Última', horas: 0 },
    { valor: '1h', etiqueta: '1 h', horas: 1 },
    { valor: '6h', etiqueta: '6 h', horas: 6 },
    { valor: '12h', etiqueta: '12 h', horas: 12 },
    { valor: '24h', etiqueta: '24 h', horas: 24 },
    { valor: '72h', etiqueta: '72 h', horas: 72 },
  ];
  public rango: Rango = 'ultimo';

  public verFotos = false;
  public indiceFoto = 0;

  /** Ver `pantallaAncha` y `altoMapa` en el detalle de tracker. */
  public pantallaAncha = signal(window.innerWidth > 900);
  public altoMapa = signal<number | null>(null);
  private observadorMapa?: ResizeObserver;

  @ViewChild('contenedorMapa') set contenedorMapa(
    ref: ElementRef<HTMLElement> | undefined,
  ) {
    this.observadorMapa?.disconnect();
    if (!ref) return;
    this.observadorMapa = new ResizeObserver(([entrada]) => {
      this.altoMapa.set(Math.round(entrada.contentRect.height) || null);
    });
    this.observadorMapa.observe(ref.nativeElement);
  }

  @HostListener('window:resize')
  public alRedimensionar(): void {
    this.pantallaAncha.set(window.innerWidth > 900);
  }

  /**
   * Arma la vista con lo que haya llegado. Se llama al recibir el activo y
   * con cada refresco de los últimos reportes.
   */
  private armarVista(): void {
    const a = this.activo;
    if (!a) return;
    const t = a.tracker;

    // Conexión con el más nuevo de cualquier tipo, como tracker y vehículos.
    const fecha = this.reporteReciente?.fechaCreacion;
    const bateria = this.bateria();

    const md = a.modoDesactivado;
    let desactivado: string | undefined;
    if (isModoDesactivadoActivo(md)) {
      desactivado =
        md?.permanente || !md?.hasta
          ? 'Sin fecha de fin'
          : `Hasta el ${HelperService.getFechaYHora(md.hasta)}`;
    }

    this.vista = {
      conexion: t
        ? estadoConexion(minutosDesde(fecha), t.frecReporte)
        : { tono: 'neutro', etiqueta: 'Sin tracker' },
      hace: fecha ? haceCuanto(fecha) : undefined,
      ultimoReporte: fecha ? HelperService.getFechaYHora(fecha) : undefined,
      bateria: bateria !== undefined ? `${Math.round(bateria)} %` : undefined,
      agrupacion: a.grupo?.nombre,
      // Sin alta lo dice el encabezado
      fechaAlta: a.fechaAlta ? HelperService.getFecha(a.fechaAlta) : undefined,
      estadoCuenta: a.estadoCuenta ? String(a.estadoCuenta) : undefined,
      desactivado,
      fotos: a.imagenes?.filter(Boolean) ?? [],
      tracker: t
        ? {
            id: t._id,
            nombre: t.nombre,
            tipo: t.tipo,
            uniqueId: t.uniqueId,
            deveui: t.tipo === 'T1000-B' ? t.t1000b?.deveui : undefined,
            frecuencia: t.frecReporte
              ? `cada ${duracion(t.frecReporte)}`
              : undefined,
          }
        : undefined,
    };
  }

  /** La batería del reporte más nuevo que la trae (solo algunos equipos). */
  private bateria(): number | undefined {
    const valores = this.reporteReciente?.valores as
      | { bateria?: number }
      | undefined;
    return typeof valores?.bateria === 'number' ? valores.bateria : undefined;
  }

  /**
   * Últimos reportes del activo, cada 30 s: hay uno por tipoReporte (y, con
   * un tracker cambiado, también los del anterior), del más nuevo al más
   * viejo.
   */
  private listarUltimosReportes(): void {
    this.reportes$?.unsubscribe();
    const filter: IFilter<IReporteGenerico> = {
      idsAsignados: this.activo?._id,
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: '-fechaCreacion',
      includeChildren: true,
    };
    this.reportes$ = this.lista2
      .ultimosReportesGenericos(query, 30)
      // Un request que falla terminaba el refresco para siempre
      .pipe(retry({ delay: () => timer(30000) }))
      .subscribe((data) => {
        const reportes = data?.datos ?? [];
        this.reporteReciente = reportes[0];
        const anterior = this.coordenadas(this.reportePosicion);
        this.reportePosicion = reportes.find((r) => this.coordenadas(r));
        this.armarVista();
        // El mapa solo se rehace si el punto se movió: si no, parpadea.
        const actual = this.coordenadas(this.reportePosicion);
        if (
          this.rango === 'ultimo' &&
          (this.loadingMapa() || actual?.join() !== anterior?.join())
        ) {
          this.mapaUltimaUbicacion();
        }
      });
  }

  private coordenadas(r?: IReporteGenerico): [number, number] | undefined {
    const geojson = (r?.valores as { geojson?: IGeoJSONPoint } | undefined)
      ?.geojson;
    const c = geojson?.coordinates;
    return c?.length === 2 ? [c[0], c[1]] : undefined;
  }

  private mapaUltimaUbicacion(): void {
    const coordenadas = this.coordenadas(this.reportePosicion);
    const marker: MarkerMapa | undefined = coordenadas
      ? { label: 'puntoSimple', coor: coordenadas, centrar: true }
      : undefined;
    this.mapCardConfig = {
      title: 'Última ubicación',
      subtitle: HelperService.extraerDireccion(
        (this.reportePosicion?.valores as { direccion?: string } | undefined)
          ?.direccion,
      ),
      icon: 'location_on',
      color: 'red',
      // Con centro y zoom: el marcador solo no mueve el mapa
      mapOptions: marker
        ? { markers: [marker], center: coordenadas, zoom: 15 }
        : undefined,
    };
    this.loadingMapa.set(false);
  }

  /** El camino de las últimas horas, con salida y llegada. */
  private async mapaRecorrido(horas: number): Promise<void> {
    this.loadingMapa.set(true);
    const hasta = new Date();
    const desde = new Date(hasta.getTime() - horas * 60 * 60 * 1000);
    const filter = {
      idsAsignados: this.activo?._id,
      'valores.timestamp': {
        $gte: desde.toISOString(),
        $lte: hasta.toISOString(),
      },
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      includeChildren: true,
      sort: 'timestamp',
    };
    let coords: [number, number][] = [];
    try {
      coords = await firstValueFrom(this.lista2.snappeadosGenericos(query));
    } catch {
      coords = [];
    }
    // Si mientras tanto se eligió otro rango, este resultado ya no va
    if (this.rango === 'ultimo' || this.horasRango() !== horas) return;

    const titulo = `Recorrido de las últimas ${horas} h`;
    if (!coords?.length) {
      this.mapCardConfig = {
        title: titulo,
        subtitle: 'Sin reportes en el período',
        icon: 'location_on',
        color: 'red',
        mapOptions: undefined,
      };
      this.loadingMapa.set(false);
      return;
    }
    const linea: LineStringMapa = {
      points: coords,
      showPoint: true,
      lastPoint: true,
      color: 'blue',
      width: 2,
      dash: true,
      arrow: true,
      centrar: true,
    };
    const salida: MarkerMapa = {
      label: 'puntoSimple',
      coor: coords[0],
      text: 'Salida',
      centrar: true,
    };
    const llegada: MarkerMapa = {
      label: 'puntoSimple',
      coor: coords[coords.length - 1],
      text: 'Llegada',
      centrar: true,
    };
    this.mapCardConfig = {
      title: titulo,
      subtitle: undefined,
      icon: 'location_on',
      color: 'red',
      mapOptions: { polylines: [linea], markers: [llegada, salida] },
    };
    this.loadingMapa.set(false);
  }

  private horasRango(): number {
    return this.rangos.find((r) => r.valor === this.rango)?.horas ?? 0;
  }

  public elegirRango(rango: Rango): void {
    if (rango === this.rango) return;
    this.rango = rango;
    if (rango === 'ultimo') {
      this.mapaUltimaUbicacion();
    } else {
      void this.mapaRecorrido(this.horasRango());
    }
  }

  public abrirFoto(indice: number): void {
    this.indiceFoto = indice;
    this.verFotos = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['activo']) return;
    // Mismo activo recargado (se editó): solo se rearma la vista
    if (this.activo?._id === this.idPedido) {
      this.armarVista();
      return;
    }
    this.idPedido = this.activo?._id;
    this.reporteReciente = undefined;
    this.reportePosicion = undefined;
    this.rango = 'ultimo';
    this.loadingMapa.set(true);
    this.armarVista();
    if (this.activo?._id) this.listarUltimosReportes();
  }

  ngOnDestroy(): void {
    this.reportes$?.unsubscribe();
    this.observadorMapa?.disconnect();
  }
}
